import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, doc, getDoc, setDoc, onSnapshot, deleteDoc, arrayUnion } from 'firebase/firestore';
import { firestore, assertFirebaseReady } from './firebaseConfig';
import ChatRoomList from './components/ChatRoomList';
import UserProfile from './components/UserProfile';
import './ChatRoomListPage.scss';
import * as S from '../scss/theme';
import fnc from '../mymod/commFunction';
import { normalizeAcaCode, normalizeChatUserId } from '../mymod/normalizeAcaCode';

const ACTIVE_CHATROOM_STORAGE_KEY = 'activeChatroomId';

const toPlainUserId = (rawId) => {
    if (!rawId || typeof rawId !== 'string') {
        return '';
    }

    const trimmedId = rawId.trim();
    if (!trimmedId) {
        return '';
    }

    const tokens = trimmedId.split('_');
    if (tokens.length >= 2 && /^(?:[A-Za-z]+\d+|\d+)$/.test(tokens[0])) {
        return tokens.slice(1).join('_');
    }

    return trimmedId;
};



const ChatRoomListPage = () => {
    const navigate = useNavigate();
    const navigationType = useNavigationType();

    const userInfo = useSelector((state) => state.reduxStore.userInfo);
    const userInfo2 = useSelector((state) => state.reduxStore.userInfo2);
    const fcmToken = useSelector((state) => state.reduxStore.fcmToken);
    const [chatrooms, setChatrooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const firebaseFeatureName = '채팅 기능';
    
    // Firebase Anonymous Auth 대신 사용자 ID 직접 사용
    const rawUserId = userInfo2?.id || userInfo?.id || '';
    const acaCode = normalizeAcaCode(
        userInfo2?.acaCode
        || userInfo?.acaCode
        || fnc.getCookie('acaCode')
        || fnc.getCookie('certCode')
    );
    const userId = normalizeChatUserId(rawUserId, acaCode);
    const plainUserId = toPlainUserId(rawUserId || userId);
    const userIdCandidates = [...new Set([userId, rawUserId, plainUserId].filter(Boolean))];

    // /talk 페이지를 브라우저 새로고침으로 직접 진입하면 로그인 화면으로 보냄
    useEffect(() => {
        if (typeof window === 'undefined' || typeof performance === 'undefined') {
            return;
        }

        const [navigationEntry] = performance.getEntriesByType('navigation');
        const isReload = navigationEntry?.type === 'reload';
        const isPopNavigation = navigationType === 'POP';

        if (isReload && isPopNavigation) {
            navigate('/login', { replace: true });
        }
    }, [navigate, navigationType]);

    // 목록 화면에서는 이전 채팅방의 읽음 리스너가 값을 지우지 못하게 활성 상태를 해제한다.
    useEffect(() => {
        try {
            sessionStorage.removeItem(ACTIVE_CHATROOM_STORAGE_KEY);
        } catch (error) {
            // 저장소를 사용할 수 없어도 목록은 정상 표시한다.
        }
    }, []);

    // 채팅방 실시간 리스너 설정
    useEffect(() => {
        if (!userIdCandidates.length) {
            setLoading(false);
            return;
        }

        if (!firestore) {
            setLoading(false);
            return;
        }

        setLoading(true);
        const unsubscribe = loadChatrooms(userIdCandidates);// 내가 참여자인 채팅방만 로드
        
        return () => {
            if (unsubscribe) unsubscribe();// 구독 해제
        };
    }, [userIdCandidates.join('|')]);

    // userInfo2 로드 확인 (개발 모드에서만)
    // 사용자 프로필 초기화 (Firebase Auth 제거)
    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        const activeUserInfo = userInfo2 || userInfo;
        if (!activeUserInfo) {
            setLoading(false);
            return;
        }

        const displayId2 = activeUserInfo?.id2 || (rawUserId && !rawUserId.includes('_') ? rawUserId : '');

        if (!firestore) {
            setLoading(false);
            return;
        }

        const initializeUserProfile = async () => {
            console.log('[사용자 프로필 초기화]', userId);
            
            try {
                // Firestore에서 사용자 정보 확인
                const userDocRef = doc(firestore, 'users', userId);
                const userDocSnap = await getDoc(userDocRef);
                console.log('[Firestore 사용자 문서]', userDocSnap.exists() ? userDocSnap.data() : '문서 없음');
                
                const userProfileData = {
                    id: userId,
                    nickname: activeUserInfo?.nickname || '',
                    id2: activeUserInfo?.id2 || '',
                    profile_image: activeUserInfo?.profile_image || '',
                    email: activeUserInfo?.email || '',
                    lastSeen: serverTimestamp(),
                    isOnline: true
                };

                if (!userDocSnap.exists()) {
                    userProfileData.createdAt = serverTimestamp();
                }

                if (fcmToken) {
                    userProfileData.fcmTokens = arrayUnion(fcmToken);
                }

                await setDoc(userDocRef, userProfileData, { merge: true });

                if (!userDocSnap.exists()) {
                    console.log('✅ 새 사용자 프로필 생성:', userId);
                } else {
                    console.log('✅ 기존 사용자 프로필 갱신:', userId);
                }
                
                setUserProfile({
                    id: userId,
                    realId: userId,
                    id2: displayId2,
                    nickname: activeUserInfo?.nickname || '',
                    email: activeUserInfo?.email || '',
                    phoneNumber: activeUserInfo?.hp || '',
                    profile_image: activeUserInfo?.profile_image || ''
                });
                
                setLoading(false);
            } catch (error) {
                console.error('프로필 초기화 오류:', error);
                setLoading(false);
            }
        };

        initializeUserProfile();
    }, [userId, rawUserId, userInfo, userInfo2, fcmToken]);

    // 채팅방 목록 실시간 리스너 (자신이 참여자인 채팅방만)
    const loadChatrooms = (candidateIds) => {
        try {
            assertFirebaseReady(firebaseFeatureName);

            if (!Array.isArray(candidateIds) || candidateIds.length === 0) {
                setChatrooms([]);
                setLoading(false);
                return null;
            }

            const snapshotStore = new Map();

            const updateMergedChatrooms = () => {
                const mergedById = new Map();

                snapshotStore.forEach((rooms) => {
                    rooms.forEach((room) => {
                        mergedById.set(room.id, room);
                    });
                });

                const mergedList = [...mergedById.values()];
                mergedList.sort((a, b) => {
                    const timeA = a.lastMessageTime?.toDate?.() || new Date(0);
                    const timeB = b.lastMessageTime?.toDate?.() || new Date(0);
                    return timeB - timeA;
                });

                setChatrooms(mergedList);
                setLoading(false);
            };

            const unsubscribeList = candidateIds.map((targetUserId) => {
                const q = query(
                    collection(firestore, 'chatRooms'),
                    where('participants', 'array-contains', targetUserId)
                );

                return onSnapshot(q, (querySnapshot) => {
                    const chatroomsList = [];
                    querySnapshot.forEach((roomDoc) => {
                        chatroomsList.push({
                            id: roomDoc.id,
                            ...roomDoc.data()
                        });
                    });

                    snapshotStore.set(targetUserId, chatroomsList);
                    updateMergedChatrooms();
                }, (error) => {
                    console.error(`채팅방 로드 오류 (${targetUserId}):`, error);
                    snapshotStore.set(targetUserId, []);
                    updateMergedChatrooms();
                });
            });

            return () => {
                unsubscribeList.forEach((unsubscribe) => {
                    if (typeof unsubscribe === 'function') {
                        unsubscribe();
                    }
                });
            };
        } catch (error) {
            console.error('채팅방 리스너 설정 오류:', error);
            if (String(error?.message || '').includes('[Firebase]')) {
                alert(error.message);
            }
            setLoading(false);
            return null;
        }
    };

    // 새 채팅방 생성
    const createChatroom = async (roomName, selectedUserIds) => {
        if (!userId) return;

        try {
            assertFirebaseReady(firebaseFeatureName);

            // 참여자 목록: 생성자 + 선택된 사용자들
            const allParticipants = [userId, ...selectedUserIds];
            
            // 모든 참여자의 unreadCount 초기화
            const unreadCount = {};
            allParticipants.forEach(participantId => {
                unreadCount[participantId] = 0;
            });

            const chatroomData = {
                name: roomName,
                participants: allParticipants,
                createdBy: userId,
                createdAt: serverTimestamp(),
                lastMessage: '',
                lastMessageTime: serverTimestamp(),
                unreadCount: unreadCount
            };

            const docRef = await addDoc(collection(firestore, 'chatRooms'), chatroomData);
            
            console.log(`✅ 채팅방 생성됨: ${roomName}, 참여자: ${allParticipants.length}명`);
            
            // 새로 생성한 채팅방으로 이동
            navigate(`/chat/${docRef.id}`);
        } catch (error) {
            console.error('채팅방 생성 오류:', error);
            alert('채팅방 생성에 실패했습니다: ' + error.message);
        }
    };

    // 채팅방 선택
    const selectChatroom = (chatroom) => {
        navigate(`/chat/${chatroom.id}`);
    };

    // 채팅방 삭제
    const deleteChatroom = async (chatroomId) => {
        try {
            assertFirebaseReady(firebaseFeatureName);
            await deleteDoc(doc(firestore, 'chatRooms', chatroomId));
        } catch (error) {
            console.error('채팅방 삭제 오류:', error);
            alert('채팅방 삭제에 실패했습니다: ' + error.message);
        }
    };

    if (loading) {
        return (
            <div className="chat-list-loading">
                <div className="loading-spinner"></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    return (
        <S.Section> 
        <div className="ChatRoomListPage">
            <UserProfile 
                user={userProfile} 
                onUpdateProfile={setUserProfile}
                currentUser={userProfile}
            />
            <ChatRoomList
                chatrooms={chatrooms}
                currentChatroom={null}
                onSelectChatroom={selectChatroom}
                onCreateChatroom={createChatroom}
                onDeleteChatroom={deleteChatroom}
                currentUserId={userId}
                currentUserIdCandidates={userIdCandidates}
            />
        </div>
        </S.Section>
    );
};

export default ChatRoomListPage;
